import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import FormBuilder from '../components/FormBuilder';
import LoadTemplateModal from '../components/LoadTemplateModal';
import SaveTemplateModal from '../components/SaveTemplateModal';
import Spinner from '../components/Spinner';
import PhotoUploader from '../components/PhotoUploader';
import CoordinatePicker from '../components/CoordinatePicker';
import Footer from '../components/Footer';
import colors from '../theme/colors';

const getTagColor = (tag) => {
  const tagColors = colors.tags[tag] || colors.tags.default;
  return `${tagColors.bg} ${tagColors.text}`;
};

const AVAILABLE_TAGS = ['T', 'E', 'EE', 'EAI', 'EEA', 'A', 'F', 'PD', 'AD', 'Facile', 'Moderatamente difficile', 'Difficile', 'Molto difficile', 'Estremamente difficile'];
const AVAILABLE_CATEGORIES = [
  'Escursionismo',
  'Alpinismo',
  'Trekking',
  'Ferrata',
  'Invernale',
  'Sci alpinismo',
  'Sci di fondo',
  'MTB',
  'Appennino',
  'Alpi',
  'Giornaliera',
  'Weekend',
  'Più giorni',
  'Rifugio',
  'Giovani',
  'Arrampicata',
  'Hotel',
  'Under 25'
];

export default function CreateHikePage() {
  const { id } = useParams(); // If id exists, we're editing
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  // Check if this is a reproposal from an archived hike
  const cloneData = location.state?.cloneFrom;
  const isReproposal = location.state?.isReproposal;

  const [loading, setLoading] = useState(false);
  const [loadingHikeData, setLoadingHikeData] = useState(false);
  const [cloneSourceHikeId, setCloneSourceHikeId] = useState(null);
  const [error, setError] = useState('');
  const [organizers, _setOrganizers] = useState([]);
  const organizersRef = useRef(organizers); // Ref to always have latest organizers
  // Wrapper to keep ref in sync synchronously
  const setOrganizers = (valueOrUpdater) => {
    _setOrganizers(prev => {
      const newValue = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      organizersRef.current = newValue;
      return newValue;
    });
  };
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  // Track email lookup status per organizer index: { exists: bool | null, loading: bool, email: string, userDetails: object | null }
  const [emailLookupStatus, setEmailLookupStatus] = useState({});
  const emailLookupTimers = useRef({});
  const [photos, setPhotos] = useState([]);
  const [formData, setFormData] = useState({
    titolo: '',
    luogo: '',
    descrizione: '',
    descrizione_breve: '',
    url_foto: '',
    data_inizio: '',
    data_fine: '',
    data_apertura_iscrizioni: '',
    data_termine_iscrizioni: '',
    richiede_approvazione: true,
    max_partecipanti: '',
    quota_cai: '',
    quota_assicurazione: '',
    quota_pernotto: '',
    quota_pernotto_non_cai: '',
    is_hidden: true,
    organizer_ids: [],
    tags: [],
    categories: [],
    metadata: {
      distanza: '',
      dislivello: ''
    },
    has_registration_form: false,
    registration_form_schema: [],
    latitude: null,
    longitude: null
  });

  const fetchHikeData = useCallback(async () => {
    setLoadingHikeData(true);
    try {
      const response = await fetch(`${API_URL}/hikes/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const hike = await response.json();
        setFormData({
          titolo: hike.titolo,
          luogo: hike.luogo,
          descrizione: hike.descrizione,
          descrizione_breve: hike.descrizione_breve,
          url_foto: hike.url_foto,
          data_inizio: hike.data_inizio,
          data_fine: hike.data_fine || '',
          data_apertura_iscrizioni: hike.data_apertura_iscrizioni || '',
          data_termine_iscrizioni: hike.data_termine_iscrizioni,
          richiede_approvazione: hike.richiede_approvazione,
          max_partecipanti: hike.max_partecipanti || '',
          quota_cai: hike.quota_cai || '',
          quota_assicurazione: hike.quota_assicurazione || '',
          quota_pernotto: hike.quota_pernotto || '',
          quota_pernotto_non_cai: hike.quota_pernotto_non_cai || '',
          is_hidden: hike.is_hidden,
          organizer_ids: hike.organizer_ids || [],
          tags: hike.tags || [],
          categories: hike.categories || [],
          metadata: hike.metadata || { distanza: '', dislivello: '' },
          has_registration_form: hike.has_registration_form || false,
          registration_form_schema: hike.registration_form_schema || [],
          latitude: hike.latitude || null,
          longitude: hike.longitude || null
        });

        // Load photos
        setPhotos(hike.photos || []);
        // Load organizers if editing
        if (hike.organizers && hike.organizers.length > 0) {
          setOrganizers(hike.organizers.map(org => ({
            id: org.id,
            user_id: org.user_id,
            email: org.email || '',
            full_name: org.full_name || '',
            display_name: org.display_name || ''
          })));
        }
      }
    } catch (err) {
      setError('Impossibile caricare i dati della gita');
    } finally {
      setLoadingHikeData(false);
    }
  }, [id, token]);

  useEffect(() => {
    if (id) {
      fetchHikeData();
    }
  }, [id, fetchHikeData]);

  // Handle reproposal - pre-fill form from cloned hike
  useEffect(() => {
    if (cloneData && !id) {
      setFormData(prev => ({
        ...prev,
        titolo: cloneData.titolo || '',
        luogo: cloneData.luogo || '',
        descrizione: cloneData.descrizione || '',
        descrizione_breve: cloneData.descrizione_breve || '',
        url_foto: '', // Photos will be copied separately
      data_inizio: '', // Clear dates - user must set new dates
         data_fine: '',
         data_apertura_iscrizioni: '',
         data_termine_iscrizioni: '',
        richiede_approvazione: cloneData.richiede_approvazione ?? true,
        max_partecipanti: cloneData.max_partecipanti || '',
        quota_cai: cloneData.quota_cai || '',
        quota_assicurazione: cloneData.quota_assicurazione || '',
        quota_pernotto: cloneData.quota_pernotto || '',
        quota_pernotto_non_cai: cloneData.quota_pernotto_non_cai || '',
        is_hidden: true, // New hikes are hidden by default
        organizer_ids: [],
        tags: cloneData.tags || [],
        categories: cloneData.categories || [],
        metadata: cloneData.metadata || { distanza: '', dislivello: '' },
        has_registration_form: cloneData.has_registration_form || false,
        registration_form_schema: cloneData.registration_form_schema || [],
        latitude: cloneData.latitude || null,
        longitude: cloneData.longitude || null
      }));

      // Set organizers from clone
      if (cloneData.organizers && cloneData.organizers.length > 0) {
        setOrganizers(cloneData.organizers.map(org => ({
          id: null, // New organizers, will be created fresh
          user_id: org.user_id,
          email: org.email || '',
          full_name: org.full_name || '',
          display_name: org.display_name || ''
        })));
      }

      // Store source hike ID for photo copying
      setCloneSourceHikeId(cloneData.id);
    }
  }, [cloneData, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMetadataChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [key]: value
      }
    }));
  };

  const handleCoordinateChange = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const toggleCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleLoadTemplate = (schema) => {
    // Generate new IDs for fields to avoid conflicts with existing fields
    const newSchema = schema.map(field => ({
      ...field,
      id: 'field_' + Math.random().toString(36).substring(2, 11)
    }));
    setFormData(prev => ({ ...prev, registration_form_schema: newSchema }));
  };

  const addOrganizer = () => {
    setOrganizers(prev => [...prev, { email: '', full_name: '' }]);
  };

  const removeOrganizer = async (index) => {
    const organizer = organizers[index];

    // If editing an existing hike and organizer has an ID, delete via API
    if (id && organizer.id) {
      try {
        const response = await fetch(`${API_URL}/hikes/${id}/organizers/${organizer.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Impossibile rimuovere l\'organizzatore');
        }
      } catch (err) {
        setError(err.message);
        return;
      }
    }

    // Remove from local state
    setOrganizers(prev => prev.filter((_, i) => i !== index));
  };

  const updateOrganizer = (index, field, value) => {
    setOrganizers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    // Trigger debounced email lookup when email field changes
    if (field === 'email') {
      debouncedEmailLookup(index, value);
    }
  };

  // Debounced email lookup function
  const debouncedEmailLookup = useCallback((index, email) => {
    // Clear any existing timer for this index
    if (emailLookupTimers.current[index]) {
      clearTimeout(emailLookupTimers.current[index]);
    }

    // If email is empty, clear the status
    if (!email || !email.trim()) {
      setEmailLookupStatus(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return;
    }

    // Set loading state immediately
    setEmailLookupStatus(prev => ({
      ...prev,
      [index]: { exists: null, loading: true, email: email, userDetails: null }
    }));

    // Set a new timer for debounced lookup
    emailLookupTimers.current[index] = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/users/check-email?email=${encodeURIComponent(email)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.exists) {
            // If user exists, get their details
            const detailsResponse = await fetch(`${API_URL}/users/get-details-by-email?email=${encodeURIComponent(email)}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (detailsResponse.ok) {
              const userDetails = await detailsResponse.json();
              setEmailLookupStatus(prev => ({
                ...prev,
                [index]: { exists: true, loading: false, email: email, userDetails: userDetails }
              }));
              
              // Auto-fill the name field if it's empty
              setOrganizers(prev => {
                const updated = [...prev];
                if (!updated[index].full_name || !updated[index].full_name.trim()) {
                  updated[index] = { 
                    ...updated[index], 
                    full_name: userDetails.full_name 
                  };
                }
                return updated;
              });
            } else {
              setEmailLookupStatus(prev => ({
                ...prev,
                [index]: { exists: true, loading: false, email: email, userDetails: null }
              }));
            }
          } else {
            setEmailLookupStatus(prev => ({
              ...prev,
              [index]: { exists: false, loading: false, email: email, userDetails: null }
            }));
          }
        } else {
          // On error, just clear the status
          setEmailLookupStatus(prev => ({
            ...prev,
            [index]: { exists: null, loading: false, email: email, userDetails: null }
          }));
        }
      } catch {
        setEmailLookupStatus(prev => ({
          ...prev,
          [index]: { exists: null, loading: false, email: email, userDetails: null }
        }));
      }
    }, 500); // 500ms debounce
  }, [token]);

  const saveOrganizer = async (index) => {
    // Use a small delay to ensure React state has been updated after any auto-fill
    await new Promise(resolve => setTimeout(resolve, 50));

    // Get the latest organizer state from ref (avoids stale closure issues)
    const organizer = organizersRef.current[index];
    if (!organizer) {
      return;
    }

    // Skip if both fields are empty
    if (!organizer.email && !organizer.full_name) {
      return;
    }

    // Only save if we're editing an existing hike
    if (id) {
      try {
        const organizerData = {
          email: organizer.email || null,
          full_name: organizer.full_name || null
        };

        let response;
        if (organizer.id) {
          // Update existing organizer
          response = await fetch(`${API_URL}/hikes/${id}/organizers/${organizer.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(organizerData)
          });
        } else {
          // Create new organizer
          response = await fetch(`${API_URL}/hikes/${id}/organizers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(organizerData)
          });
        }

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Impossibile salvare l\'organizzatore');
        }

        const savedOrganizer = await response.json();

        // Update local state with the saved organizer
        setOrganizers(prev => {
          const updated = [...prev];
          updated[index] = {
            id: savedOrganizer.id,
            user_id: savedOrganizer.user_id,
            email: savedOrganizer.email || '',
            full_name: savedOrganizer.full_name || '',
            display_name: savedOrganizer.display_name || ''
          };
          return updated;
        });
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Helper to copy photos from source hike (for reproposals)
  const copyPhotosFromHike = async (sourceHikeId, targetHikeId) => {
    try {
      const response = await fetch(`${API_URL}/hikes/${targetHikeId}/copy-photos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ source_hike_id: sourceHikeId })
      });

      if (!response.ok) {
        console.warn('Failed to copy photos, continuing...');
      } else {
        const result = await response.json();
        console.log(`Copied ${result.copied} photos`);
      }
    } catch (err) {
      console.warn('Error copying photos:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate organizers before submission
    for (let i = 0; i < organizers.length; i++) {
      const organizer = organizers[i];
      const status = emailLookupStatus[i];
      
      // If organizer has an email but it doesn't exist in the system
      if (organizer.email && organizer.email.trim() && status?.exists === false) {
        setError(`Non è possibile salvare: l'indirizzo email "${organizer.email}" non è registrato nel sistema. Se l'organizzatore non è registrato, lasciare vuoto il campo email e compilare solo il nome.`);
        setLoading(false);
        return;
      }
    }

    try {
      // Prepare metadata as array of key-value pairs
      const metadataArray = Object.entries(formData.metadata)
        .filter(([_, value]) => value !== '')
        .map(([key, value]) => ({ key, value }));

      const hikeData = {
        ...formData,
        max_partecipanti: formData.max_partecipanti ? parseInt(formData.max_partecipanti) : null,
        quota_cai: formData.quota_cai ? parseFloat(formData.quota_cai) : null,
        quota_assicurazione: formData.quota_assicurazione ? parseFloat(formData.quota_assicurazione) : null,
        quota_pernotto: formData.quota_pernotto ? parseFloat(formData.quota_pernotto) : null,
        quota_pernotto_non_cai: formData.quota_pernotto_non_cai ? parseFloat(formData.quota_pernotto_non_cai) : null,
        data_fine: formData.data_fine || null,
        metadata: metadataArray,
        organizer_ids: formData.organizer_ids || [], // Preserve existing organizers during edit
        has_registration_form: formData.has_registration_form,
        registration_form_schema: formData.has_registration_form ? formData.registration_form_schema : null
      };

      const url = id ? `${API_URL}/hikes/${id}` : `${API_URL}/hikes`;
      const method = id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(hikeData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Impossibile salvare la gita');
      }

      const savedHike = await response.json();

      // If creating a new hike and there are organizers to add, add them via the new API
      if (!id && organizers.length > 0) {
        for (const organizer of organizers) {
          // Skip empty organizers
          if (!organizer.email && !organizer.full_name) {
            continue;
          }

          try {
            const organizerResponse = await fetch(`${API_URL}/hikes/${savedHike.id}/organizers`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                email: organizer.email || null,
                full_name: organizer.full_name || null
              })
            });

            if (!organizerResponse.ok) {
              console.error('Failed to add organizer:', organizer);
            }
          } catch (err) {
            console.error('Error adding organizer:', err);
          }
        }
      }

      // If this is a reproposal, copy photos from original hike
      if (isReproposal && cloneSourceHikeId) {
        await copyPhotosFromHike(cloneSourceHikeId, savedHike.id);
      }

      navigate(`/hike/${savedHike.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-4">
            {id ? 'Modifica Gita' : isReproposal ? 'Riproponi Gita' : 'Crea Nuova Gita'}
          </h1>

          {isReproposal && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
              Stai riproponendo la gita "{cloneData?.titolo}". Le date sono state svuotate - inserisci le nuove date, modifica i dettagli se necessario, poi salva. Le foto verranno copiate automaticamente.
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-4">
              {error}
            </div>
          )}

          {loadingHikeData ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Spinner size="md" color="blue" />
              <p className="mt-3 text-sm text-gray-600">Caricamento...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
            {/* Basic Information */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Informazioni Base</h2>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Titolo *
                </label>
                <input
                  type="text"
                  name="titolo"
                  value={formData.titolo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Luogo *
                </label>
                <input
                  type="text"
                  name="luogo"
                  value={formData.luogo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>

              {/* GPS Coordinates Picker */}
              <CoordinatePicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                onChange={handleCoordinateChange}
                disabled={false}
              />

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Descrizione Breve *
                </label>
                <input
                  type="text"
                  name="descrizione_breve"
                  value={formData.descrizione_breve}
                  onChange={handleChange}
                  placeholder="Una breve descrizione (max 100 caratteri)"
                  maxLength={100}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Descrizione Completa *
                </label>
                <textarea
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={handleChange}
                  rows={6}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>

              {/* Photo uploader - replaces url_foto input */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Foto Gita
                </label>
                <PhotoUploader
                  hikeId={id}
                  photos={photos}
                  onPhotosChange={setPhotos}
                  maxPhotos={10}
                  token={token}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {id ? 'Carica fino a 10 foto per la gita.' : 'Potrai caricare le foto dopo aver creato la gita.'}
                </p>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Date</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Inizio *
                  </label>
                  <input
                    type="date"
                    name="data_inizio"
                    value={formData.data_inizio}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data Fine (se di più giorni)
                  </label>
                  <input
                    type="date"
                    name="data_fine"
                    value={formData.data_fine}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Apertura Iscrizioni
                  </label>
                  <input
                    type="date"
                    name="data_apertura_iscrizioni"
                    value={formData.data_apertura_iscrizioni}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Termine Iscrizioni *
                  </label>
                  <input
                    type="date"
                    name="data_termine_iscrizioni"
                    value={formData.data_termine_iscrizioni}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Participants & Costs */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Partecipanti e Quote</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Max Partecipanti
                  </label>
                  <input
                    type="number"
                    name="max_partecipanti"
                    value={formData.max_partecipanti}
                    onChange={handleChange}
                    min="1"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="richiede_approvazione"
                    id="richiede_approvazione"
                    checked={formData.richiede_approvazione}
                    onChange={handleChange}
                    className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                  />
                  <label htmlFor="richiede_approvazione" className="ml-2 text-xs font-medium text-gray-700">
                    Richiede Approvazione Organizzatori
                  </label>
                </div>
              </div>

              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  name="has_registration_form"
                  id="has_registration_form"
                  checked={formData.has_registration_form}
                  onChange={handleChange}
                  className="w-4 h-4 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                />
                <label htmlFor="has_registration_form" className="ml-2 text-xs font-medium text-gray-700">
                  Richiedi compilazione modulo all'iscrizione
                </label>
              </div>

              {/* Registration Form Builder */}
              {formData.has_registration_form && (
                <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-800">
                      Modulo di Iscrizione
                    </h3>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowLoadTemplateModal(true)}
                        className="px-2.5 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                      >
                        Carica da modello
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSaveTemplateModal(true)}
                        disabled={formData.registration_form_schema.length === 0}
                        className="px-2.5 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Salva come modello
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Configura le domande che i partecipanti dovranno compilare al momento dell'iscrizione.
                  </p>
                  <FormBuilder
                    fields={formData.registration_form_schema}
                    onChange={(fields) => setFormData(prev => ({ ...prev, registration_form_schema: fields }))}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Quota CAI (€)
                  </label>
                  <input
                    type="number"
                    name="quota_cai"
                    value={formData.quota_cai}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Quota Assicurazione (€)
                    <span className="text-xs text-gray-500 ml-1">(solo non soci)</span>
                  </label>
                  <input
                    type="number"
                    name="quota_assicurazione"
                    value={formData.quota_assicurazione}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pernotto Soci (€)
                  </label>
                  <input
                    type="number"
                    name="quota_pernotto"
                    value={formData.quota_pernotto}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Pernotto Non Soci (€)
                  </label>
                  <input
                    type="number"
                    name="quota_pernotto_non_cai"
                    value={formData.quota_pernotto_non_cai}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="Uguale a soci"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Dettagli Tecnici</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Distanza
                  </label>
                  <input
                    type="text"
                    value={formData.metadata.distanza}
                    onChange={(e) => handleMetadataChange('distanza', e.target.value)}
                    placeholder="es. 15.2 km"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Dislivello
                  </label>
                  <input
                    type="text"
                    value={formData.metadata.dislivello}
                    onChange={(e) => handleMetadataChange('dislivello', e.target.value)}
                    placeholder="es. 850 m"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Tag Difficoltà</h2>
              <div className="flex flex-wrap gap-1.5">
                {AVAILABLE_TAGS.map(tag => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                      formData.tags.includes(tag)
                        ? `${getTagColor(tag)} ring-2 ring-offset-1 ring-current`
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Categorie</h2>
              <div className="flex flex-wrap gap-1.5">
                {AVAILABLE_CATEGORIES.map(category => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                      formData.categories.includes(category)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Organizers */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold text-gray-800">Organizzatori</h2>
              <p className="text-xs text-gray-600">
                Aggiungi organizzatori per questa gita. Puoi specificare l'email (per associarli a utenti registrati) o il nome completo se non sono iscritti al sito.
              </p>

              {organizers.length === 0 ? (
                <div className="text-xs text-gray-500 italic">
                  Nessun organizzatore aggiunto. Clicca "Aggiungi Organizzatore" per aggiungerne uno.
                </div>
              ) : (
                <div className="space-y-2">
                  {organizers.map((organizer, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded border border-gray-200">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-1">
                            <span>Email (se iscritto al sito)</span>
                            {/* Email lookup status indicator */}
                            {organizer.email && organizer.email.trim() && emailLookupStatus[index] && (
                              <span className={`flex items-center gap-1 text-xs ${
                                emailLookupStatus[index].loading
                                  ? 'text-gray-400'
                                  : emailLookupStatus[index].exists
                                    ? 'text-green-600'
                                    : 'text-red-400'
                              }`}>
                                {emailLookupStatus[index].loading ? (
                                  <>
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifica...
                                  </>
                                ) : emailLookupStatus[index].exists ? (
                                  <>
                                    Trovato
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </>
                                ) : (
                                  <>
                                    Non trovato
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  </>
                                )}
                              </span>
                            )}
                          </label>
                          <input
                            type="email"
                            value={organizer.email}
                            onChange={(e) => updateOrganizer(index, 'email', e.target.value)}
                            onBlur={() => saveOrganizer(index)}
                            placeholder="email@esempio.it"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Nome Completo (se non iscritto al sito)
                          </label>
                          <input
                            type="text"
                            value={organizer.full_name}
                            onChange={(e) => updateOrganizer(index, 'full_name', e.target.value)}
                            onBlur={() => saveOrganizer(index)}
                            placeholder="Mario Rossi"
                            className={`w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition ${
                              emailLookupStatus[index]?.exists ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            disabled={emailLookupStatus[index]?.exists}
                            title={emailLookupStatus[index]?.exists ? 'Il nome è stato automaticamente compilato dai dati dell\'utente registrato' : ''}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOrganizer(index)}
                        className="mt-5 p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                        title="Rimuovi organizzatore"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={addOrganizer}
                className="w-full py-2 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition"
              >
                + Aggiungi Organizzatore
              </button>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-100">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-2.5 rounded text-sm font-medium transition ${
                  loading
                    ? 'bg-blue-300 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Salvataggio...' : (id ? 'Salva Modifiche' : 'Crea Gita')}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition"
              >
                Annulla
              </button>
            </div>
          </form>
          )}
        </div>
      </div>

      {/* Template Modals */}
      {showLoadTemplateModal && (
        <LoadTemplateModal
          token={token}
          onLoad={handleLoadTemplate}
          onClose={() => setShowLoadTemplateModal(false)}
        />
      )}
      {showSaveTemplateModal && (
        <SaveTemplateModal
          token={token}
          formSchema={formData.registration_form_schema}
          onClose={() => setShowSaveTemplateModal(false)}
        />
      )}

      <Footer />
    </div>
  );
}
